import React, {FC} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../libs/shared/core/src/helpers/internationalization/react';
import {ChannelTypesEnum} from '../../../libs/shared/core/src/models/api/channelCommonTypes';
import {ButtonVariantsEnum} from '../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {BasicDialog} from '../../../libs/shared/ui/src/components/dialogs/basicDialog';
import {SettingsResourcePagesEnum, SettingsRootPagesEnum} from '../../models/url/models/types';
import {buildSettingsPath} from '../../models/url/settingsUrlHelpers';
import {IntegrationIconsEnum} from '../common/integrationIcon';
import {CellsEmptySetupActionsButton} from '../inboxes/cells/content/cellsEmpty/cellsEmptySetupActionsButton';

/*
 * Props.
 */

interface StartGuideAddEmailModalProps {
  navigate: (destination: string) => () => void;
  onDismiss: () => void;
  userNamespace: string;
  isSending?: boolean;
  isCommenting?: boolean;
}

/*
 * Style.
 */

const StyledDiv = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
`;

const StyledButtonWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding-top: 25px;
`;

/*
 * Component.
 */

export const StartGuideAddEmailModal: FC<StartGuideAddEmailModalProps> = (props) => (
  <BasicDialog
    width={400}
    title={<Trans id="start-guide-add-email-title">Add your work email</Trans>}
    onRequestClose={props.onDismiss}
  >
    <StyledDiv>
      {renderSubtitle(props.isSending, props.isCommenting)}
      {props.userNamespace && (
        <StyledButtonWrapper>
          <CellsEmptySetupActionsButton
            buttonVariant={ButtonVariantsEnum.PRIMARY}
            interactionId="start-guide-add-email-googlebutton"
            title={<Trans id="start-guide-add-email-googlebutton">Add with Google</Trans>}
            integrationIcon={IntegrationIconsEnum.GOOGLE}
            onClick={props.navigate(
              buildSettingsPath({
                namespace: props.userNamespace,
                rootPage: SettingsRootPagesEnum.CHANNELS,
                resourcePage: SettingsResourcePagesEnum.NEW,
                channelType: ChannelTypesEnum.GMAIL,
              }),
            )}
          />
          <CellsEmptySetupActionsButton
            buttonVariant={ButtonVariantsEnum.PRIMARY}
            interactionId="start-guide-add-email-microsoftbutton"
            title={<Trans id="start-guide-add-email-microsoftbutton">Add with Microsoft</Trans>}
            integrationIcon={IntegrationIconsEnum.MICROSOFT}
            onClick={props.navigate(
              buildSettingsPath({
                namespace: props.userNamespace,
                rootPage: SettingsRootPagesEnum.CHANNELS,
                resourcePage: SettingsResourcePagesEnum.NEW,
                channelType: ChannelTypesEnum.OFFICE365,
              }),
            )}
          />
          <CellsEmptySetupActionsButton
            buttonVariant={ButtonVariantsEnum.SECONDARY}
            interactionId="start-guide-add-email-otherbutton"
            title={<Trans id="start-guide-add-email-otherbutton">Other email account</Trans>}
            onClick={props.navigate(
              buildSettingsPath({
                namespace: props.userNamespace,
                rootPage: SettingsRootPagesEnum.CHANNELS,
                resourcePage: SettingsResourcePagesEnum.NEW,
                channelType: ChannelTypesEnum.SMTP,
              }),
            )}
          />
        </StyledButtonWrapper>
      )}
    </StyledDiv>
  </BasicDialog>
);

function renderSubtitle(isSending?: boolean, isCommenting?: boolean) {
  if (isSending) {
    return (
      <Trans id="start-guide-add-email-subtitlesending">
        To send your first email, add your work email to Front
        <br /> (e.g. sophia@company.com)
      </Trans>
    );
  }

  if (isCommenting) {
    return (
      <Trans id="start-guide-add-email-subtitlecommenting">
        To leave a comment, you need access to emails in Front. Add your work email (e.g. sophia@company.com)
      </Trans>
    );
  }

  return (
    <Trans id="start-guide-add-email-subtitle">
      Add your work email to Front
      <br /> (e.g. sophia@company.com)
    </Trans>
  );
}
